/**
 * PersonalizedTSService
 * 
 * Service for managing personalized time series mappings (qts_<name>).
 * Provides methods to save, check, and list user-level data mappings.
 */

import config from '../config/api';

export interface SavePersonalizedTSRequest {
  name: string;  // The qts_name (canonical form: qts_<name>)
  data_id: string;  // The data_id to save
  description?: string;  // Optional description
}

export interface SavePersonalizedTSResponse {
  success: boolean;
  qts_name: string;  // Full name with qts_ prefix
  action: string;  // "created" or "updated"
  message?: string;
}

export interface CheckNameExistsResponse {
  exists: boolean;
  qts_name: string;
}

export interface PersonalizedTSMapping {
  qts_name: string;
  data_id: string;
  data_description?: string;
  created_at: string;
  updated_at: string;
}

export interface ListPersonalizedTSResponse {
  mappings: PersonalizedTSMapping[];
  count: number;
}

export interface DeletePersonalizedTSResponse {
  success: boolean;
  qts_name: string;
  message: string;
}

export class PersonalizedTSService {
  private baseUrl = config.apiUrl;

  /**
   * Save a personalized time series mapping.
   * Creates or updates a mapping from qts_<name> to a data_id.
   */
  async saveMapping(
    userUuid: string,
    token: string,
    request: SavePersonalizedTSRequest
  ): Promise<SavePersonalizedTSResponse> {
    const response = await fetch(
      `${this.baseUrl}/user/${userUuid}/personalized_ts`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to save personalized TS: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Check if a personalized TS name already exists.
   * Used to determine whether to show "Save" or "Overwrite" button.
   */
  async checkNameExists(
    userUuid: string,
    token: string,
    name: string
  ): Promise<CheckNameExistsResponse> {
    const response = await fetch(
      `${this.baseUrl}/user/${userUuid}/personalized_ts/check/${encodeURIComponent(name)}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to check name: ${response.status}`);
    }

    return response.json();
  }

  /**
   * List all personalized TS mappings for a user.
   */
  async listMappings(
    userUuid: string,
    token: string
  ): Promise<ListPersonalizedTSResponse> {
    const response = await fetch(
      `${this.baseUrl}/user/${userUuid}/personalized_ts`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to list mappings: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Delete a specific personalized TS mapping by name.
   * The name should include the qts_ prefix (e.g., qts_my_data).
   */
  async deleteMapping(
    userUuid: string,
    token: string,
    name: string
  ): Promise<DeletePersonalizedTSResponse> {
    const response = await fetch(
      `${this.baseUrl}/user/${userUuid}/personalized_ts/${encodeURIComponent(name)}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to delete mapping: ${response.status}`);
    }

    return response.json();
  }
}

// Export singleton instance
export const personalizedTSService = new PersonalizedTSService();
